import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  SectionList,
  Text,
  View,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { trpc } from '../lib/trpc';
import { Button } from './Button';
import { TextInput } from './TextInput';
import { FormErrorBanner } from './FormErrorBanner';
import { formatCurrency } from '../lib/utils';
import {
  buildCategorySections,
  filterCategorySections,
  type Category,
} from './transaction-categories';
import { webPrivacyProps } from '../lib/privacy';

export interface CategoryPickerModalProps {
  visible: boolean;
  onClose: () => void;
  selectedCategoryId: number | null;
  onSelect: (categoryId: number | null) => void;
  title?: string;
  subtitle?: string;
  /**
   * When provided, the picker exposes a "Link to life event" view. Omit both
   * props to hide the life-event affordance entirely.
   */
  selectedGoalId?: string | null;
  onSelectGoal?: (goalId: string | null) => void;
  /** Restrict the list to expense budget categories (life-event tracking). */
  expenseBudgetOnly?: boolean;
  /** Label for the trailing "no category" row (default "Uncategorized"). */
  nullOptionLabel?: string;
}

function toTestIdSegment(value: string | number | null | undefined): string {
  return String(value ?? 'none')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function CategoryPickerModal({
  visible,
  onClose,
  selectedCategoryId,
  onSelect,
  title,
  subtitle,
  selectedGoalId,
  onSelectGoal,
  expenseBudgetOnly = false,
  nullOptionLabel,
}: CategoryPickerModalProps) {
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'category' | 'goal' | 'editCategory'>('category');
  // id === null → creating a new category; otherwise renaming an existing one
  const [editCategory, setEditCategory] = useState<{ id: number | null; name: string }>({
    id: null,
    name: '',
  });
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const { height: windowHeight } = useWindowDimensions();
  const goalPickerEnabled = onSelectGoal !== undefined;
  const categoriesQ = trpc.budget.listCategories.useQuery();
  const utils = trpc.useUtils();

  const invalidateCategoryData = () => {
    // Category names/ids surface in budget entries, transaction lists, rules
    // and check-ins — refresh the lot rather than tracking each consumer.
    void utils.budget.invalidate();
    void utils.transactions.invalidate();
    void utils.categoryRules.invalidate();
  };

  const createCategoryM = trpc.budget.createCategory.useMutation({
    onSuccess: (created) => {
      invalidateCategoryData();
      setView('category');
      setSearch('');
      if (created) onSelect(created.id);
    },
    onError: (e) => setCategoryError(e.message),
  });
  const updateCategoryM = trpc.budget.updateCategory.useMutation({
    onSuccess: () => {
      invalidateCategoryData();
      setView('category');
    },
    onError: (e) => setCategoryError(e.message),
  });
  const deleteCategoryM = trpc.budget.deleteCategory.useMutation({
    onSuccess: () => {
      invalidateCategoryData();
      if (editCategory.id != null && selectedCategoryId === editCategory.id) {
        onSelect(null);
      }
      setView('category');
    },
    onError: (e) => setCategoryError(e.message),
  });
  const categoryMutationPending =
    createCategoryM.isPending || updateCategoryM.isPending || deleteCategoryM.isPending;

  const openCategoryEditor = (target: { id: number | null; name: string }) => {
    setEditCategory(target);
    setCategoryError(null);
    setConfirmingDelete(false);
    createCategoryM.reset();
    updateCategoryM.reset();
    deleteCategoryM.reset();
    setView('editCategory');
  };

  const saveCategory = () => {
    const name = editCategory.name.trim();
    if (!name) {
      setCategoryError('Enter a category name.');
      return;
    }
    setCategoryError(null);
    if (editCategory.id == null) {
      createCategoryM.mutate({ name });
    } else {
      updateCategoryM.mutate({ id: editCategory.id, name });
    }
  };
  const categories = useMemo(() => {
    const all = (categoriesQ.data ?? []) as Category[];
    return expenseBudgetOnly
      ? all.filter((c) => c.isBudget && c.type === 'expense')
      : all;
  }, [categoriesQ.data, expenseBudgetOnly]);
  const eventsQ = trpc.fire.listEvents.useQuery(undefined, {
    enabled: visible && goalPickerEnabled,
  });

  // Reset search + view when modal opens/closes
  useEffect(() => {
    if (!visible) {
      setSearch('');
      setView('category');
      setCategoryError(null);
      setConfirmingDelete(false);
      return;
    }
    if (Platform.OS !== 'web') return;
    if (view !== 'category') return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Backspace') {
        setSearch((s) => s.slice(0, -1));
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        setSearch((s) => s + e.key);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [visible, view, onClose]);

  const sections = useMemo(
    () =>
      filterCategorySections(
        buildCategorySections(categories, selectedCategoryId, nullOptionLabel),
        search,
      ),
    [categories, selectedCategoryId, search, nullOptionLabel],
  );
  const modalMaxHeight = Math.min(windowHeight * 0.7, 680);
  const listMaxHeight = Math.max(modalMaxHeight - 132, 240);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View className="flex-1 bg-black/40 justify-end">
        <Pressable className="absolute inset-0" onPress={onClose} />
        <View
          className="bg-white dark:bg-gray-800 rounded-t-2xl"
          style={{ maxHeight: modalMaxHeight }}
        >
          <View className="flex-row items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
            <View className="flex-1 pr-4 flex-row items-center gap-2">
              {view !== 'category' && (
                <Pressable onPress={() => setView('category')} hitSlop={8}>
                  <Text className="text-[15px] text-green-600 dark:text-green-400">
                    ←
                  </Text>
                </Pressable>
              )}
              <View className="flex-1">
                {title && (
                  <Text
                    className="text-sm font-semibold text-gray-800 dark:text-gray-200"
                    numberOfLines={1}
                    {...webPrivacyProps('private')}
                  >
                    {title}
                  </Text>
                )}
                <Text className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {view === 'goal'
                    ? 'Link to a life event'
                    : view === 'editCategory'
                    ? editCategory.id == null
                      ? 'New custom category'
                      : 'Edit custom category'
                    : search
                    ? `Search: "${search}"`
                    : subtitle ?? 'Select a category — type to filter'}
                </Text>
              </View>
            </View>
            <Button variant="ghost" size="sm" onPress={onClose}>
              Done
            </Button>
          </View>

          {view === 'category' ? (
            <View>
              <View className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
                <TextInput
                  testID="category-picker-search-input"
                  accessibilityLabel="Search categories"
                  value={search}
                  onChangeText={setSearch}
                  placeholder="Search categories"
                  autoCapitalize="none"
                  autoCorrect={false}
                  size="sm"
                />
              </View>
              <SectionList
                sections={sections}
                keyExtractor={(item) => String(item.id ?? 'none')}
                contentContainerStyle={{ paddingBottom: 32 }}
                style={{ maxHeight: listMaxHeight }}
                keyboardShouldPersistTaps="handled"
                ListHeaderComponent={
                  goalPickerEnabled ? (
                    <Pressable
                      testID="category-picker-life-event-option"
                      accessibilityLabel="Link transaction to life event"
                      onPress={() => setView('goal')}
                      className="flex-row items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700"
                    >
                      <View className="flex-1">
                        <Text className="text-sm text-green-700 dark:text-green-400 font-medium">
                          Link to life event…
                        </Text>
                        <Text className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {selectedGoalId
                            ? 'Currently linked — tap to change or unlink'
                            : 'Exclude from monthly budget and apply to a life event'}
                        </Text>
                      </View>
                      <Text className="text-gray-300 dark:text-gray-600">›</Text>
                    </Pressable>
                  ) : null
                }
                renderSectionHeader={({ section }) =>
                  section.title ? (
                    <View className="px-5 py-1.5 bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700">
                      <Text className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                        {section.title}
                      </Text>
                    </View>
                  ) : null
                }
                renderItem={({ item: cat }) => {
                  const isSelected = selectedCategoryId === cat.id;
                  return (
                    <Pressable
                      testID={`category-picker-option-${toTestIdSegment(cat.name)}`}
                      accessibilityLabel={`Category option ${cat.name}`}
                      onPress={() => onSelect(cat.id as number | null)}
                      className={`flex-row items-center justify-between px-5 py-3.5 border-b border-gray-50 dark:border-gray-700 ${
                        isSelected ? 'bg-green-50 dark:bg-green-900/30' : ''
                      }`}
                    >
                      <Text
                        className={`text-sm ${
                          isSelected
                            ? 'text-green-700 dark:text-green-400 font-semibold'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {cat.name}
                      </Text>
                      <View className="flex-row items-center gap-3">
                        {cat.isCustom && cat.id != null && (
                          <Pressable
                            testID={`category-picker-edit-${toTestIdSegment(cat.name)}`}
                            accessibilityLabel={`Edit category ${cat.name}`}
                            hitSlop={8}
                            onPress={() =>
                              openCategoryEditor({ id: cat.id, name: cat.name })
                            }
                          >
                            <Text className="text-xs text-gray-400 dark:text-gray-500">
                              Edit
                            </Text>
                          </Pressable>
                        )}
                        {isSelected && (
                          <Text className="text-green-600 text-sm">✓</Text>
                        )}
                      </View>
                    </Pressable>
                  );
                }}
                ListFooterComponent={
                  <Pressable
                    testID="category-picker-new-category"
                    accessibilityLabel="Create a new category"
                    onPress={() => openCategoryEditor({ id: null, name: search.trim() })}
                    className="flex-row items-center px-5 py-3.5"
                  >
                    <Text className="text-sm text-green-700 dark:text-green-400 font-medium">
                      + New category{search.trim() ? ` "${search.trim()}"` : ''}
                    </Text>
                  </Pressable>
                }
              />
            </View>
          ) : view === 'editCategory' ? (
            <View className="px-5 py-4" style={{ maxHeight: listMaxHeight }}>
              <TextInput
                testID="category-editor-name-input"
                accessibilityLabel="Category name"
                value={editCategory.name}
                onChangeText={(name) => setEditCategory((c) => ({ ...c, name }))}
                placeholder="Category name (e.g. Subscriptions)"
                autoFocus
                maxLength={50}
                editable={!categoryMutationPending}
              />
              {categoryError ? (
                <View className="mt-3">
                  <FormErrorBanner
                    error={categoryError}
                    onDismiss={() => setCategoryError(null)}
                  />
                </View>
              ) : null}
              <View className="flex-row gap-2 mt-4">
                <Button
                  size="sm"
                  onPress={saveCategory}
                  loading={createCategoryM.isPending || updateCategoryM.isPending}
                  disabled={categoryMutationPending}
                >
                  {editCategory.id == null ? 'Create' : 'Save'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onPress={() => setView('category')}
                  disabled={categoryMutationPending}
                >
                  Cancel
                </Button>
              </View>
              {editCategory.id != null && (
                <View className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                  {confirmingDelete ? (
                    <View>
                      <Text className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        Transactions in this category will become uncategorized,
                        and any budget amounts or rules for it will be removed.
                      </Text>
                      <View className="flex-row gap-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onPress={() =>
                            deleteCategoryM.mutate({ id: editCategory.id! })
                          }
                          loading={deleteCategoryM.isPending}
                          disabled={categoryMutationPending}
                        >
                          Delete category
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onPress={() => setConfirmingDelete(false)}
                          disabled={categoryMutationPending}
                        >
                          Keep
                        </Button>
                      </View>
                    </View>
                  ) : (
                    <Pressable
                      onPress={() => setConfirmingDelete(true)}
                      disabled={categoryMutationPending}
                      hitSlop={8}
                    >
                      <Text className="text-sm text-red-600 dark:text-red-500">
                        Delete this category…
                      </Text>
                    </Pressable>
                  )}
                </View>
              )}
            </View>
          ) : (
            <ScrollView
              contentContainerStyle={{ paddingBottom: 32 }}
              style={{ maxHeight: listMaxHeight }}
            >
              {eventsQ.isLoading ? (
                <Text className="text-sm text-gray-500 py-6 text-center">
                  Loading…
                </Text>
              ) : (eventsQ.data?.length ?? 0) === 0 ? (
                <Text className="text-sm text-gray-500 py-6 text-center">
                  No life events yet — add one from the FIRE plan.
                </Text>
              ) : (
                eventsQ.data!.map((g) => {
                  const isSelected = selectedGoalId === g.id;
                  const label = g.description || `${g.type} in ${g.year}`;
                  return (
                    <Pressable
                      key={g.id}
                      onPress={() => onSelectGoal!(g.id)}
                      className={`flex-row items-center justify-between px-5 py-3.5 border-b border-gray-50 dark:border-gray-700 ${
                        isSelected ? 'bg-green-50 dark:bg-green-900/30' : ''
                      }`}
                    >
                      <View className="flex-1 pr-3">
                        <Text
                          className={`text-sm ${
                            isSelected
                              ? 'text-green-700 dark:text-green-400 font-semibold'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                          {...webPrivacyProps('private')}
                        >
                          {label}
                        </Text>
                        <Text
                          className="text-xs text-gray-400 dark:text-gray-500 mt-0.5"
                          {...webPrivacyProps('financial')}
                        >
                          {g.year}
                          {g.amount ? ` · ${formatCurrency(Number(g.amount))}` : ''}
                        </Text>
                      </View>
                      {isSelected && (
                        <Text className="text-green-600 text-sm">✓</Text>
                      )}
                    </Pressable>
                  );
                })
              )}
              {selectedGoalId && (
                <View className="px-5 pt-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() => onSelectGoal!(null)}
                  >
                    Unlink
                  </Button>
                </View>
              )}
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );
}
